import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const stats = [
  { label: '收录h漫作品', value: '500+', icon: 'ri-book-2-fill' },
  { label: '内容分类', value: '8', icon: 'ri-folder-2-fill' },
  { label: '累计读者', value: '10万+', icon: 'ri-user-heart-line' },
  { label: '更新频率', value: '每周', icon: 'ri-refresh-line' },
];

const values = [
  {
    title: '专业精选',
    description: '我们的编辑团队深耕h漫领域多年，以专业眼光为你筛选优质内容，确保每篇推荐都经过精心考量。拒绝低质内容，只推送值得阅读的h漫佳作。',
    icon: 'ri-star-fill',
  },
  {
    title: '内容全面',
    description: '覆盖日本h漫、汉化h漫、同人h漫、3D h漫、全彩h漫等丰富品类，无论你偏好哪种风格，都能在这里找到心仪内容。持续更新，紧跟h漫潮流。',
    icon: 'ri-stack-fill',
  },
  {
    title: '文化解读',
    description: '我们不只做导航，更致力于h漫文化的深度解读。从创作背景、画师风格到行业趋势，提供多维度的内容分析，帮助读者更深入地理解h漫。',
    icon: 'ri-lightbulb-fill',
  },
  {
    title: '用户至上',
    description: '以读者需求为核心，持续优化导航体验。分类清晰、搜索便捷、推荐精准，让你把时间花在享受优质h漫上，而非在无穷列表里寻找内容。',
    icon: 'ri-heart-3-fill',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符。');
      return;
    }

    setFormStatus('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qspi6e3oq4jqumtq7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }).toString(),
      });
      const responseText = await res.text();
      if (res.ok) {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { parsed = null; }
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试。';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于h漫导航',
    description: 'h漫导航品牌介绍、口碑评价与联系方式。精选h漫资源导航与推荐平台。',
    url: 'https://hmanga.example.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'h漫导航是什么类型的网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'h漫导航是一个专注于h漫资源聚合、分类导航与内容推荐的专业平台。我们提供h漫导览、热门作品推荐、画师介绍及相关文化解读，帮助读者快速找到优质的h漫内容。',
        },
      },
      {
        '@type': 'Question',
        name: 'h漫导航有哪些功能特色？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们提供八大专区分类（日本h漫、汉化h漫、同人h漫、3D h漫等），精选文章推荐，每周持续更新h漫相关内容。读者可按分类浏览或通过标签精准查找所需h漫资源。',
        },
      },
      {
        '@type': 'Question',
        name: 'h漫导航是否收费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'h漫导航完全免费使用。我们致力于为h漫爱好者提供优质的资源导航和内容推荐服务，所有功能无需付费即可使用。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系h漫导航团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页面的联系表单提交留言，我们的团队会尽快回复。也欢迎通过社交媒体渠道与我们交流h漫相关内容。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          {/* Header */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="mt-6 text-center max-w-2xl mx-auto">
                <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">
                  关于h漫导航
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  专注h漫资源导航与推荐，致力于为h漫爱好者打造便捷、专业的内容发现平台
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading text-foreground-950 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">
                  品牌介绍
                </h2>
                <div className="space-y-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">h漫导航</strong>成立于2023年，是一个专注于<strong>h漫资源聚合</strong>与推荐的专业内容平台。我们深知在浩瀚的h漫海洋中找到优质内容并不容易，因此致力于为h漫爱好者提供清晰、精准的导航服务。
                  </p>
                  <p>
                    我们的编辑团队具备深厚的<strong>h漫鉴赏经验</strong>，持续追踪h漫领域的优秀作品与新兴趋势。无论是经典的<strong>日本h漫</strong>、精彩的<strong>同人h漫</strong>创作，还是技术驱动的<strong>3D h漫</strong>作品，我们都会以专业角度进行筛选与推荐。
                  </p>
                  <p>
                    我们相信，每一位h漫读者都值得获得更好的内容发现体验。h漫导航不仅是一个资源索引，更是一个汇聚h漫文化爱好者的社区——在这里，你可以发现新的<strong>h漫作品</strong>，了解创作背后的故事，与同好交流心得。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-10 text-center">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="flex gap-4 p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-10 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: 'h漫导航是什么类型的网站？', a: 'h漫导航是一个专注于h漫资源聚合、分类导航与内容推荐的专业平台。我们提供h漫导览、热门作品推荐、画师介绍及相关文化解读，帮助读者快速找到优质的h漫内容。' },
                  { q: '网站上的h漫资源如何获取？', a: 'h漫导航作为导航平台，提供h漫作品的推荐和分类索引。你可以通过各分类页面浏览感兴趣的h漫类型，阅读我们的推荐文章获取详细资讯。' },
                  { q: 'h漫导航是否收费？', a: 'h漫导航完全免费使用。我们致力于为h漫爱好者提供优质的资源导航和内容推荐服务，所有功能无需付费即可使用。' },
                  { q: '网站上的内容多久更新一次？', a: '我们的编辑团队会持续更新h漫推荐和资讯内容，通常在每周都会有新的文章和推荐上线。你可以订阅我们的资讯推送，第一时间获取更新通知。' },
                  { q: '如何联系h漫导航团队？', a: '你可以通过本页面的联系表单提交留言，我们的团队会尽快回复。也欢迎通过社交媒体渠道与我们交流h漫相关内容。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70">
                    <summary className="flex items-center justify-between p-5 cursor-pointer text-sm md:text-base font-medium text-foreground-900">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何建议、合作意向或问题反馈？欢迎通过以下表单与我们取得联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5 bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hidden-absolute"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    你的名字
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                  <span className="text-foreground-400 font-normal ml-2">
                    ({formData.message.length}/500)
                  </span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你的留言内容（不超过500字符）"
                />
              </div>
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formMessage && (
                <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}